import styled, {
  system,
  type SystemProps,
} from 'quickstart/styled-components/system'
import type {ComponentProps} from 'react'
import {useReskin} from '../base/hooks'

const StyledButton = styled.button<SystemProps>`
  ${system}
`

interface ButtonProps extends ComponentProps<typeof StyledButton> {
  /** `.t-button`'s own axes, passed through as `data-*`. Reskin only. */
  variant?: 'primary' | 'danger'
  size?: 'small' | 'medium' | 'large'
  shape?: 'pill'
}

/**
 * A button.
 *
 * Under the reskin this is always a real `<button class="t-button">`. Classic
 * keeps the fork below, and the comment explaining it is worth reading as
 * history rather than as a rule: `<input type=button>` was used *because*
 * classic's CSS styled that and not `<button>`, with `<button>` as the
 * fallback for non-string children. `.t-button` is class-driven and
 * element-agnostic, so the reason evaporates and the fork with it
 * (design.md — Decision 5).
 *
 * `variant`, `size` and `shape` are `.t-button`'s three independent axes.
 * No call site passes any of them today; they are here because the class has
 * them and a call site that wants one should reach for the admin's vocabulary
 * rather than a style prop.
 *
 * Styled-system props still work on the reskin branch, which is why this keeps
 * `StyledButton` underneath rather than rendering a plain element: both `Add`
 * buttons pass `mt`, and that is per-call-site spacing rather than the classic
 * look — the same line `A.Row`'s `maxWidth` sits on (task 2.3).
 */
export const Button = ({
  children,
  shape,
  size,
  variant,
  ...props
}: ButtonProps) => {
  if (useReskin()) {
    return (
      <StyledButton
        className="t-button"
        type="button"
        {...(variant && {'data-variant': variant})}
        {...(size && {'data-size': size})}
        {...(shape && {'data-shape': shape})}
        {...props}
      >
        {children}
      </StyledButton>
    )
  }
  // This uses <input type=button> by default because admin css uses that, and
  // doesn't provide any styling for <button>. We have to fall back to <button>
  // if children is something other than a string, though.
  return typeof children === 'string' ? (
    <StyledButton
      as="input"
      type="button"
      className="button"
      value={children}
      {...props}
    />
  ) : (
    <StyledButton type="button" {...props}>
      {children}
    </StyledButton>
  )
}
